import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { trNorm } from '@/lib/arama';

const typeLabels = {
  raw_material: 'Hammadde', semi_finished: 'Yari Mamul',
  finished: 'Mamul', packaging: 'Ambalaj', other: 'Diger'
};
const typeColors = {
  raw_material: 'bg-[#3d6e4e]/10 text-[#3d6e4e]', semi_finished: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  finished: 'bg-[#2e2d29]/10 text-[#2e2d29]', packaging: 'bg-[#8a7e70]/10 text-[#8a7e70]', other: 'bg-[#d0c4b0] text-[#3F3E39]'
};

export default function WarehouseDetail() {
  // Stok miktarı `stock_quantity` iznine bağlı (sunucu da göndermez)
  const { hasPermission } = useAuth();
  const stokGorebilir = hasPermission('stock_quantity', 'view');
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/warehouses');
  const [warehouse, setWarehouse] = useState(null);
  usePageTitle(warehouse?.name || 'Depo Detayı');
  const [stockItems, setStockItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [subcatFilter, setSubcatFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get(`/warehouses/${id}`).then(r => setWarehouse(r.data)).catch(() => toast.error('Depo yuklenemedi'));
    api.get(`/warehouses/${id}/stock`).then(r => { setStockItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  const parentCategories = categories.filter(c => !c.parent_id);
  const filteredSubcats = categories.filter(c => c.parent_id === catFilter);

  useEffect(() => { setSubcatFilter('all'); }, [catFilter]);

  const filtered = stockItems.filter(s => {
    if (search) {
      const sl = trNorm(search);
      if (!trNorm(s.product_name).includes(sl) && !trNorm(s.product_code).includes(sl)) return false;
    }
    if (typeFilter !== 'all' && s.product_type !== typeFilter) return false;
    if (catFilter !== 'all' && s.category_id !== catFilter) return false;
    if (subcatFilter !== 'all' && s.subcategory_id !== subcatFilter) return false;
    return true;
  });

  if (!warehouse) return <div className="text-center py-8 text-[#8a7e70]">Yukleniyor...</div>;

  return (
    <div data-testid="warehouse-detail-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]" data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">{warehouse.name}</h1>
            <p className="text-sm text-[#8a7e70] mt-1">{filtered.length} urun listeleniyor</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
          <Input placeholder="Urun ara..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white border-[#d0c4b0]" data-testid="warehouse-search-input" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44 bg-white border-[#d0c4b0]" data-testid="warehouse-type-filter">
            <SelectValue placeholder="Tur filtrele" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Turler</SelectItem>
            <SelectItem value="raw_material">Hammadde</SelectItem>
            <SelectItem value="semi_finished">Yari Mamul</SelectItem>
            <SelectItem value="finished">Mamul</SelectItem>
            <SelectItem value="packaging">Ambalaj</SelectItem>
            <SelectItem value="other">Diger</SelectItem>
          </SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-44 bg-white border-[#d0c4b0]" data-testid="warehouse-category-filter">
            <SelectValue placeholder="Kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Kategoriler</SelectItem>
            {parentCategories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={subcatFilter} onValueChange={setSubcatFilter} disabled={catFilter === 'all'}>
          <SelectTrigger className={`w-44 bg-white border-[#d0c4b0] ${catFilter === 'all' ? 'opacity-50' : ''}`}
            data-testid="warehouse-subcategory-filter">
            <SelectValue placeholder="Alt Kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Alt Kategoriler</SelectItem>
            {filteredSubcats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">Kod</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">Urun Adi</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">Tur</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">Kategori</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">Alt Kategori</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">Birim</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider text-right">Stok</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider text-right">Son Fiyat</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((s) => (
              <TableRow key={s.id} className="erp-table-row border-[#d0c4b0]">
                <TableCell className="font-mono text-sm text-[#3F3E39]">{s.product_code}</TableCell>
                <TableCell>
                  <Link to={`/erp/products/${s.product_card_id}`} className="text-[#1a1916] font-medium hover:underline"
                    data-testid={`stock-product-link-${s.product_card_id}`}>
                    {s.product_name}
                  </Link>
                </TableCell>
                <TableCell>
                  <span className={`status-badge ${typeColors[s.product_type] || ''}`}>
                    {typeLabels[s.product_type] || s.product_type || '-'}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{s.category_name || '-'}</TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{s.subcategory_name || '-'}</TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{s.product_unit || s.unit}</TableCell>
                <TableCell className="text-right font-semibold text-[#1a1916]">
                  {stokGorebilir ? s.quantity?.toFixed(2) : <span className="text-[#b5aa9a] italic font-normal text-xs">gizli</span>}
                </TableCell>
                <TableCell className="text-right font-mono text-sm text-[#3F3E39]">
                  {s.last_purchase_price ? `${s.last_purchase_price.toFixed(2)} TL` : '-'}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-[#8a7e70]">
                  {loading ? 'Yukleniyor...' : 'Urun bulunamadi'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
